import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "mall-ship__shell" }
const _hoisted_2 = {
  key: 0,
  class: "mall-ship__error"
}
const _hoisted_3 = {
  key: 1,
  class: "mall-ship__toast"
}
const _hoisted_4 = { class: "mall-ship__card" }
const _hoisted_5 = { class: "mall-ship__card-head" }
const _hoisted_6 = {
  key: 0,
  class: "mall-ship__empty"
}
const _hoisted_7 = {
  key: 1,
  class: "mall-ship__empty"
}
const _hoisted_8 = {
  key: 2,
  class: "mall-ship__list"
}
const _hoisted_9 = { class: "mall-ship__item-body" }
const _hoisted_10 = { class: "mall-ship__item-top" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { class: "mall-ship__item-actions" }
const _hoisted_13 = ["disabled", "onClick"]
const _hoisted_14 = ["disabled", "onClick"]
const _hoisted_15 = ["disabled", "onClick"]
const _hoisted_16 = {
  key: 2,
  class: "mall-ship__card"
}
const _hoisted_17 = { class: "mall-ship__card-head" }
const _hoisted_18 = { class: "mall-ship__row" }
const _hoisted_19 = { class: "mall-ship__zip" }
const _hoisted_20 = ["value"]
const _hoisted_21 = ["disabled"]
const _hoisted_22 = ["value"]
const _hoisted_23 = { class: "mall-ship__check" }
const _hoisted_24 = { class: "mall-ship__form-actions" }
const _hoisted_25 = ["disabled"]
const _hoisted_26 = { class: "mall-ship__card" }
const _hoisted_27 = { class: "mall-ship__presets" }
const _hoisted_28 = ["onClick"]
const _hoisted_29 = { class: "mall-ship__form-actions" }
const _hoisted_30 = ["disabled"]

import { onMounted, reactive, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { useDaumPostcode } from '@/composables/useDaumPostcode'
import { useMallMypageEmbedded } from '@/composables/useMallMypage'
import { useMemberAuth } from '@/composables/useMemberAuth'
import {
  createShippingAddress,
  deleteShippingAddress,
  fetchShippingBook,
  saveShippingMemo,
  updateShippingAddress,
  type MallShippingAddress,
} from '@/api/mallShipping'


export default /*@__PURE__*/_defineComponent({
  __name: 'MallShippingPage',
  setup(__props) {

const MEMO_PRESETS = [
  '배송 전 연락 바랍니다',
  '문 앞에 놓아주세요',
  '경비실에 맡겨주세요',
  '직접 받겠습니다',
] as const

const { member } = useMemberAuth()
const { opening: postcodeOpening, searchPostcode } = useDaumPostcode()
const isEmbedded = useMallMypageEmbedded()

const addresses = ref<MallShippingAddress[]>([])
const memo = ref('')
const loading = ref(true)
const busy = ref(false)
const editorOpen = ref(false)
const editingIdx = ref<number | null>(null)
const errorMessage = ref('')
const toastMessage = ref('')

const form = reactive({
  label: '',
  receiver: '',
  phone: '',
  zipcode: '',
  address: '',
  addressDetail: '',
  isDefault: true,
})

function showToast(message: string) {
  toastMessage.value = message
  window.setTimeout(() => {
    if (toastMessage.value === message) toastMessage.value = ''
  }, 2200)
}

async function reload() {
  loading.value = true
  errorMessage.value = ''
  try {
    const book = await fetchShippingBook()
    addresses.value = book.addresses
    memo.value = book.pref.lastMemo || ''
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '배송지를 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function startCreate() {
  editingIdx.value = null
  form.label = ''
  form.receiver = member.value?.bossName || member.value?.orgName || ''
  form.phone = member.value?.bossPhone || member.value?.orgPhone || ''
  form.zipcode = ''
  form.address = ''
  form.addressDetail = ''
  form.isDefault = addresses.value.length === 0
  editorOpen.value = true
}

function startEdit(item: MallShippingAddress) {
  editingIdx.value = item.idx
  form.label = item.label
  form.receiver = item.receiver
  form.phone = item.phone
  form.zipcode = item.zipcode
  form.address = item.address
  form.addressDetail = item.addressDetail
  form.isDefault = item.isDefault
  editorOpen.value = true
}

function closeEditor() {
  editorOpen.value = false
  editingIdx.value = null
}

async function onSearchAddress() {
  try {
    const picked = await searchPostcode()
    if (!picked) return
    form.zipcode = picked.zipcode
    form.address = picked.extra ? `${picked.address} ${picked.extra}` : picked.address
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '주소 검색에 실패했습니다.'
  }
}

async function onSaveAddress() {
  if (busy.value) return
  busy.value = true
  errorMessage.value = ''
  try {
    const payload = {
      label: form.label || '기본 배송지',
      receiver: form.receiver,
      phone: form.phone,
      zipcode: form.zipcode,
      address: form.address,
      addressDetail: form.addressDetail,
      isDefault: form.isDefault,
    }
    if (editingIdx.value) {
      await updateShippingAddress(editingIdx.value, payload)
      showToast('배송지를 수정했습니다.')
    } else {
      await createShippingAddress(payload)
      showToast('배송지를 저장했습니다.')
    }
    closeEditor()
    await reload()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '배송지 저장에 실패했습니다.'
  } finally {
    busy.value = false
  }
}

async function onSetDefault(item: MallShippingAddress) {
  if (busy.value) return
  busy.value = true
  errorMessage.value = ''
  try {
    await updateShippingAddress(item.idx, {
      label: item.label,
      receiver: item.receiver,
      phone: item.phone,
      zipcode: item.zipcode,
      address: item.address,
      addressDetail: item.addressDetail,
      isDefault: true,
    })
    showToast('기본 배송지로 설정했습니다.')
    await reload()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '기본 배송지 설정에 실패했습니다.'
  } finally {
    busy.value = false
  }
}

async function onDelete(item: MallShippingAddress) {
  if (busy.value) return
  if (!window.confirm(`「${item.label}」 배송지를 삭제할까요?`)) return
  busy.value = true
  errorMessage.value = ''
  try {
    await deleteShippingAddress(item.idx)
    if (editingIdx.value === item.idx) closeEditor()
    showToast('배송지를 삭제했습니다.')
    await reload()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '배송지를 삭제하지 못했습니다.'
  } finally {
    busy.value = false
  }
}

async function onSaveMemo() {
  if (busy.value) return
  busy.value = true
  errorMessage.value = ''
  try {
    const pref = await saveShippingMemo(memo.value)
    memo.value = pref.lastMemo || ''
    showToast('요청사항을 저장했습니다.')
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '요청사항 저장에 실패했습니다.'
  } finally {
    busy.value = false
  }
}

onMounted(reload)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["mall-ship", { 'is-embedded': _unref(isEmbedded) }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("header", { class: "mall-ship__head" }, [
        _cache[6] || (_cache[6] = _createElementVNode("div", null, [
          _createElementVNode("h1", null, "배송지 관리"),
          _createElementVNode("p", null, "주문/결제 시 바로 쓸 배송지와 기본 요청사항을 관리합니다.")
        ], -1)),
        _createElementVNode("button", {
          type: "button",
          class: "mall-ship__primary",
          onClick: startCreate
        }, " 새 배송지 ")
      ]),
      (errorMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_2, [
            _createVNode(AppIcon, {
              icon: "mdi-alert-outline",
              size: 16
            }),
            _createTextVNode(" " + _toDisplayString(errorMessage.value), 1)
          ]))
        : _createCommentVNode("", true),
      (toastMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(toastMessage.value), 1))
        : _createCommentVNode("", true),
      _createElementVNode("section", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _cache[7] || (_cache[7] = _createElementVNode("h2", null, "저장된 배송지", -1)),
          _createElementVNode("span", null, _toDisplayString(addresses.value.length) + " / 10", 1)
        ]),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[8] || (_cache[8] = [
              _createElementVNode("p", null, "불러오는 중입니다…", -1)
            ]))]))
          : (!addresses.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createVNode(AppIcon, {
                  icon: "mdi-map-marker-off-outline",
                  size: 40
                }),
                _cache[9] || (_cache[9] = _createElementVNode("p", null, "저장된 배송지가 없습니다. 자주 쓰는 주소를 등록해 두세요.", -1))
              ]))
            : (_openBlock(), _createElementBlock("ul", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(addresses.value, (item) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: item.idx,
                    class: "mall-ship__item"
                  }, [
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("strong", null, _toDisplayString(item.label), 1),
                        (item.isDefault)
                          ? (_openBlock(), _createElementBlock("em", _hoisted_11, "기본"))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("p", null, _toDisplayString(item.receiver) + " · " + _toDisplayString(item.phone), 1),
                      _createElementVNode("p", null, [
                        (item.zipcode)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createTextVNode("[" + _toDisplayString(item.zipcode) + "] ", 1)
                            ], 64))
                          : _createCommentVNode("", true),
                        _createTextVNode(" " + _toDisplayString(item.address) + " ", 1),
                        (item.addressDetail)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              _createTextVNode(_toDisplayString(item.addressDetail), 1)
                            ], 64))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_12, [
                      (!item.isDefault)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            type: "button",
                            class: "mall-ship__ghost",
                            disabled: busy.value,
                            onClick: ($event: any) => (onSetDefault(item))
                          }, " 기본으로 ", 8, _hoisted_13))
                        : _createCommentVNode("", true),
                      _createElementVNode("button", {
                        type: "button",
                        class: "mall-ship__ghost",
                        disabled: busy.value,
                        onClick: ($event: any) => (startEdit(item))
                      }, " 수정 ", 8, _hoisted_14),
                      _createElementVNode("button", {
                        type: "button",
                        class: "mall-ship__ghost is-danger",
                        disabled: busy.value,
                        onClick: ($event: any) => (onDelete(item))
                      }, " 삭제 ", 8, _hoisted_15)
                    ])
                  ]))
                }), 128))
              ]))
      ]),
      (editorOpen.value)
        ? (_openBlock(), _createElementBlock("section", _hoisted_16, [
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("h2", null, _toDisplayString(editingIdx.value ? '배송지 수정' : '새 배송지'), 1),
              _createElementVNode("button", {
                type: "button",
                class: "mall-ship__text",
                onClick: closeEditor
              }, "닫기")
            ]),
            _createElementVNode("form", {
              class: "mall-ship__form",
              onSubmit: _withModifiers(onSaveAddress, ["prevent"])
            }, [
              _createElementVNode("label", null, [
                _cache[10] || (_cache[10] = _createElementVNode("span", null, "배송지 이름", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.label) = $event)),
                  maxlength: "40",
                  placeholder: "예: 본원 · 분원 · 물류창고"
                }, null, 512), [
                  [
                    _vModelText,
                    form.label,
                    void 0,
                    { trim: true }
                  ]
                ])
              ]),
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("label", null, [
                  _cache[11] || (_cache[11] = _createElementVNode("span", null, "수령인", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.receiver) = $event)),
                    required: "",
                    maxlength: "50",
                    placeholder: "이름"
                  }, null, 512), [
                    [
                      _vModelText,
                      form.receiver,
                      void 0,
                      { trim: true }
                    ]
                  ])
                ]),
                _createElementVNode("label", null, [
                  _cache[12] || (_cache[12] = _createElementVNode("span", null, "연락처", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.phone) = $event)),
                    required: "",
                    maxlength: "30",
                    placeholder: "010-0000-0000"
                  }, null, 512), [
                    [
                      _vModelText,
                      form.phone,
                      void 0,
                      { trim: true }
                    ]
                  ])
                ])
              ]),
              _createElementVNode("label", null, [
                _cache[13] || (_cache[13] = _createElementVNode("span", null, "주소", -1)),
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("input", {
                    value: form.zipcode,
                    type: "text",
                    placeholder: "우편번호",
                    readonly: "",
                    onClick: onSearchAddress
                  }, null, 8, _hoisted_20),
                  _createElementVNode("button", {
                    type: "button",
                    class: "mall-ship__ghost",
                    disabled: _unref(postcodeOpening),
                    onClick: onSearchAddress
                  }, " 주소 검색 ", 8, _hoisted_21)
                ]),
                _createElementVNode("input", {
                  value: form.address,
                  type: "text",
                  required: "",
                  placeholder: "주소 검색을 눌러 주세요",
                  readonly: "",
                  onClick: onSearchAddress
                }, null, 8, _hoisted_22),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.addressDetail) = $event)),
                  type: "text",
                  maxlength: "80",
                  placeholder: "상세 주소 (동·호수 등)"
                }, null, 512), [
                  [
                    _vModelText,
                    form.addressDetail,
                    void 0,
                    { trim: true }
                  ]
                ])
              ]),
              _createElementVNode("label", _hoisted_23, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.isDefault) = $event)),
                  type: "checkbox"
                }, null, 512), [
                  [_vModelCheckbox, form.isDefault]
                ]),
                _cache[14] || (_cache[14] = _createElementVNode("span", null, "기본 배송지로 사용", -1))
              ]),
              _createElementVNode("div", _hoisted_24, [
                _createElementVNode("button", {
                  type: "button",
                  class: "mall-ship__ghost",
                  onClick: closeEditor
                }, "취소"),
                _createElementVNode("button", {
                  type: "submit",
                  class: "mall-ship__primary",
                  disabled: busy.value
                }, _toDisplayString(busy.value ? '저장 중…' : '배송지 저장'), 9, _hoisted_25)
              ])
            ], 32)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("section", _hoisted_26, [
        _cache[16] || (_cache[16] = _createElementVNode("div", { class: "mall-ship__card-head" }, [
          _createElementVNode("h2", null, "기본 요청사항")
        ], -1)),
        _createElementVNode("form", {
          class: "mall-ship__form",
          onSubmit: _withModifiers(onSaveMemo, ["prevent"])
        }, [
          _createElementVNode("div", _hoisted_27, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(MEMO_PRESETS, (preset) => {
              return _createElementVNode("button", {
                key: preset,
                type: "button",
                class: _normalizeClass(["mall-ship__chip", { 'is-active': memo.value === preset }]),
                onClick: ($event: any) => (memo.value = preset)
              }, _toDisplayString(preset), 11, _hoisted_28)
            }), 64))
          ]),
          _createElementVNode("label", null, [
            _cache[15] || (_cache[15] = _createElementVNode("span", null, "요청사항", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((memo).value = $event)),
              maxlength: "255",
              placeholder: "배송 시 요청사항을 입력해 주세요"
            }, null, 512), [
              [
                _vModelText,
                memo.value,
                void 0,
                { trim: true }
              ]
            ])
          ]),
          _createElementVNode("div", _hoisted_29, [
            _createElementVNode("button", {
              type: "submit",
              class: "mall-ship__primary",
              disabled: busy.value
            }, _toDisplayString(busy.value ? '저장 중…' : '요청사항 저장'), 9, _hoisted_30)
          ])
        ], 32)
      ])
    ])
  ], 2))
}
}

})